/**
 * Automatic Free Gift engine
 * ---------------------------------------------------------------------------
 * Keeps free gift line items in sync with the cart, driven entirely by theme
 * settings. Nothing here is hard coded — the configuration is rendered by
 * `snippets/free-gift-config.liquid` into a JSON <script> tag and read below.
 *
 * Responsibilities:
 *   - Decide which gift(s) the current cart should contain (rule evaluation).
 *   - Add gifts that are missing, remove gifts that no longer apply, collapse
 *     duplicates and clamp gift quantities back to 1.
 *   - Expose helpers so the cart UI can identify and label gift lines.
 *
 * It deliberately does NOT render anything. `layout/theme.liquid` subscribes
 * via `GoodrFreeGift.onChange()` and re-renders the drawer when the cart moves.
 *
 * ---------------------------------------------------------------------------
 * ADDING A NEW RULE TYPE
 * ---------------------------------------------------------------------------
 *   1. Add the settings for it in `config/settings_schema.json`.
 *   2. Emit a rule object from `snippets/free-gift-config.liquid` with a new
 *      `"type"` value.
 *   3. Register a handler here (or from any script loaded after this one):
 *
 *        GoodrFreeGift.registerRule('cart_total', function (rule, context) {
 *          return context.totalPrice >= rule.threshold ? rule.gift : null;
 *        });
 *
 *   A handler receives (rule, context) and returns a gift object or null.
 *   `context` = { cart, lines, quantity, totalPrice } where `lines` and
 *   `quantity` exclude gift lines, so a gift can never qualify itself.
 *
 * NOTE: This adds the gift as a normal line item. Making it actually free
 * requires a matching automatic discount in Shopify Admin — the Ajax API
 * cannot set a line price to $0.
 */
(function (window, document) {
  "use strict";

  var LEGACY_GIFT_PROPERTY = "is_gift"; // property used by the pre-refactor implementation
  var MAX_SYNC_PASSES = 3; // convergence guard, prevents add/remove ping-pong

  var config = readConfig();
  var GIFT_PROPERTY = config.giftProperty || "_free_gift";

  var state = {
    inFlight: null, // Promise of the sync currently running
    status: null, // 'adding' | 'removing' | null — drives the drawer spinner
    blocked: {}, // variantId -> true, gifts that failed to add (sold out etc.)
    listeners: [],
  };

  var ruleHandlers = {};

  /* Config*/

  function readConfig() {
    var fallback = {
      enabled: false,
      rules: [],
      maxGifts: 1,
      cartLabel: "FREE GIFT!",
    };
    var el = document.getElementById("goodr-free-gift-config");
    if (!el) return fallback;

    try {
      var parsed = JSON.parse(el.textContent);
      parsed.rules = Array.isArray(parsed.rules) ? parsed.rules : [];
      parsed.maxGifts = Math.max(1, Number(parsed.maxGifts) || 1);
      return parsed;
    } catch (e) {
      console.error(
        "[FreeGift] Invalid configuration JSON, feature disabled.",
        e,
      );
      return fallback;
    }
  }

  function log() {
    if (!config.debug) return;
    console.log.apply(
      console,
      ["[FreeGift]"].concat(Array.prototype.slice.call(arguments)),
    );
  }

  /* ------------------------------------------------------------------ *
   * Cart line helpers
   * ------------------------------------------------------------------ */

  function normalize(value) {
    return String(value == null ? "" : value)
      .trim()
      .toLowerCase();
  }

  function isGiftLine(line) {
    if (!line || !line.properties) return false;
    return (
      line.properties[GIFT_PROPERTY] === "true" ||
      line.properties[LEGACY_GIFT_PROPERTY] === "true"
    );
  }

  /**
   * Label shown in place of the price for a gift line. A mapping entry may
   * carry its own label; otherwise the global "Cart drawer label" setting wins.
   */
  function labelFor(line) {
    var perLine =
      line && line.properties && line.properties["_free_gift_label"];
    return perLine || config.cartLabel || "FREE GIFT!";
  }

  function typeMatches(line, key, matchMode) {
    var type = normalize(line.product_type);
    var needle = normalize(key);
    if (!needle) return false;
    return matchMode === "exact"
      ? type === needle
      : type.indexOf(needle) !== -1;
  }

  function matcherMatches(line, matcher, matchMode) {
    if (!matcher || !matcher.key) return false;
    if (matcher.kind === "handle")
      return normalize(line.handle) === normalize(matcher.key);
    if (matcher.kind === "product")
      return String(line.product_id) === String(matcher.key).trim();
    return typeMatches(line, matcher.key, matchMode);
  }

  /**
   * A mapping is one "line of conditions": several matchers (e.g. OG and
   * Mach G) sharing a single gift. It matches when any one matcher matches.
   */
  function lineMatchesMapping(line, mapping, matchMode) {
    if (!mapping || !Array.isArray(mapping.matchers)) return false;
    return mapping.matchers.some(function (matcher) {
      return matcherMatches(line, matcher, matchMode);
    });
  }

  /* Rule handlers */

  function registerRule(type, handler) {
    if (typeof handler !== "function") return;
    ruleHandlers[type] = handler;
  }

  /**
   * Rule type 1 — a qualifying product type (or handle / product id) is in the
   * cart.
   *
   * Each cart line resolves to at most one gift: the first mapping it matches,
   * in configured order (advanced mapping lines, then gift groups), so the most
   * specific rule should sit highest. When qualifying lines resolve to
   * different gifts, `multiMatch` decides between the first of them and the
   * configured default gift.
   */
  registerRule("product_type", function (rule, context) {
    var matchMode = rule.matchMode || "contains";
    var mappings = Array.isArray(rule.mappings) ? rule.mappings : [];
    var triggerTypes = Array.isArray(rule.triggerTypes)
      ? rule.triggerTypes
      : [];
    var candidates = [];

    context.lines.forEach(function (line) {
      for (var i = 0; i < mappings.length; i++) {
        if (
          mappings[i] &&
          mappings[i].gift &&
          lineMatchesMapping(line, mappings[i], matchMode)
        ) {
          candidates.push(mappings[i].gift);
          return;
        }
      }

      // No group or mapping covers this line — a bare "Trigger product types"
      // entry still qualifies it for the default gift.
      var isTrigger = triggerTypes.some(function (type) {
        return typeMatches(line, type, matchMode);
      });
      if (isTrigger && rule.gift) candidates.push(rule.gift);
    });

    if (!candidates.length) return null;

    var unique = dedupeGifts(candidates);
    if (unique.length === 1) return unique[0];

    log("multiple qualifying products map to different gifts", unique);
    if (rule.multiMatch === "first") return unique[0];
    return rule.gift || unique[0];
  });

  /**
   * Rule type 2 — the cart holds N or more items (gifts excluded from count).
   */
  registerRule("cart_quantity", function (rule, context) {
    var threshold = Number(rule.threshold) || 0;
    if (threshold <= 0) return null;
    return context.quantity >= threshold ? rule.gift : null;
  });

  function dedupeGifts(gifts) {
    var seen = {};
    var out = [];
    gifts.forEach(function (gift) {
      if (!gift || !gift.variantId) return;
      var id = String(gift.variantId);
      if (seen[id]) return;
      seen[id] = true;
      out.push(gift);
    });
    return out;
  }

  // Rule evaluation

  /**
   * @returns {Array<{gift: Object, ruleId: String}>} the gifts the cart should
   * contain right now, in rule priority order and capped at `maxGifts`.
   */
  function resolveGifts(cart) {
    var lines = (cart.items || []).filter(function (line) {
      return !isGiftLine(line);
    });

    var context = {
      cart: cart,
      lines: lines,
      quantity: lines.reduce(function (sum, line) {
        return sum + (Number(line.quantity) || 0);
      }, 0),
      totalPrice: lines.reduce(function (sum, line) {
        return (
          sum +
          (Number(
            line.final_line_price != null
              ? line.final_line_price
              : line.line_price,
          ) || 0)
        );
      }, 0),
    };

    // Never gift an otherwise empty cart.
    if (!lines.length) return [];

    var resolved = [];
    var seen = {};

    config.rules.forEach(function (rule) {
      if (!rule || !rule.enabled) return;

      var handler = ruleHandlers[rule.type];
      if (!handler) {
        console.warn(
          '[FreeGift] No handler registered for rule type "' + rule.type + '"',
        );
        return;
      }

      var gift;
      try {
        gift = handler(rule, context);
      } catch (e) {
        console.error('[FreeGift] Rule "' + rule.id + '" threw, skipping.', e);
        return;
      }

      if (!gift || !gift.variantId) return;

      var id = String(gift.variantId);
      if (seen[id] || state.blocked[id]) return;
      seen[id] = true;
      resolved.push({ gift: gift, ruleId: rule.id });
    });

    return resolved.slice(0, config.maxGifts);
  }

  /* ------------------------------------------------------------------ *
   * Plan + execution
   * ------------------------------------------------------------------ */

  /**
   * Diffs the gift lines currently in the cart against the gifts the rules
   * want. Returns `null` when the cart is already correct.
   */
  function buildPlan(cart) {
    // When the feature is switched off, the only job left is cleaning up gifts
    // still sitting in customer carts from the finished promotion.
    var desired = config.enabled ? resolveGifts(cart) : [];
    var desiredIds = desired.map(function (entry) {
      return String(entry.gift.variantId);
    });

    var updates = {};
    var hasUpdates = false;
    var kept = {};

    (cart.items || []).forEach(function (line) {
      if (!isGiftLine(line)) return;

      var id = String(line.variant_id);
      var wanted = desiredIds.indexOf(id) !== -1;

      if (!wanted || kept[id]) {
        // No longer valid, or a duplicate of a gift we already keep.
        updates[line.key] = 0;
        hasUpdates = true;
        return;
      }

      kept[id] = true;
      if (Number(line.quantity) !== 1) {
        updates[line.key] = 1; // customer bumped the quantity — clamp it back
        hasUpdates = true;
      }
    });

    var additions = desired.filter(function (entry) {
      return !kept[String(entry.gift.variantId)];
    });

    if (!hasUpdates && !additions.length) return null;
    return { updates: hasUpdates ? updates : null, additions: additions };
  }

  function postJSON(path, body) {
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/";
    return fetch(root + path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error(
            (data && (data.description || data.message)) ||
              "Cart request failed",
          );
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  function fetchCart() {
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/";
    return fetch(root + "cart.js", {
      headers: { Accept: "application/json" },
    }).then(function (response) {
      return response.json();
    });
  }

  function executePlan(plan) {
    var chain = Promise.resolve(null);

    if (plan.updates) {
      log("removing / clamping gift lines", plan.updates);
      chain = chain.then(function () {
        return postJSON("cart/update.js", { updates: plan.updates });
      });
    }

    if (plan.additions.length) {
      log(
        "adding gifts",
        plan.additions.map(function (entry) {
          return entry.gift.handle;
        }),
      );
      chain = chain.then(function () {
        return postJSON("cart/add.js", {
          items: plan.additions.map(function (entry) {
            var properties = {};
            properties[GIFT_PROPERTY] = "true";
            properties["_free_gift_rule"] = entry.ruleId;
            if (entry.gift.label)
              properties["_free_gift_label"] = entry.gift.label;
            return {
              id: entry.gift.variantId,
              quantity: 1,
              properties: properties,
            };
          }),
        });
      });
      chain = chain.catch(function (error) {
        // A gift that cannot be added (sold out, deleted) is blocked for the
        // rest of the session so we do not retry it on every cart update.
        plan.additions.forEach(function (entry) {
          state.blocked[String(entry.gift.variantId)] = true;
        });
        console.warn(
          "[FreeGift] Could not add gift, it will be skipped for this session.",
          error,
        );
        return fetchCart();
      });
    }

    // `cart/add.js` returns the added items, not the cart, so always resolve
    // with an authoritative cart object.
    return chain.then(function () {
      return fetchCart();
    });
  }

  /* ------------------------------------------------------------------ *
   * Public sync
   * ------------------------------------------------------------------ */

  function statusFor(plan) {
    return plan.additions.length ? "adding" : "removing";
  }

  /**
   * Brings the cart in line with the configured rules.
   *
   * @param  {Object}  cart  A cart.js payload. Fetched automatically if absent.
   * @return {Promise<Object|null>} The updated cart, or null when nothing changed.
   */
  function sync(cart) {
    // Coalesce concurrent callers onto the sync already running.
    if (state.inFlight) return state.inFlight;

    if (!cart) {
      state.inFlight = fetchCart()
        .then(function (fetched) {
          state.inFlight = null;
          return sync(fetched);
        })
        .catch(function (error) {
          state.inFlight = null;
          console.error("[FreeGift] Could not read cart.", error);
          return null;
        });
      return state.inFlight;
    }

    var plan = buildPlan(cart);
    if (!plan) return Promise.resolve(null);

    state.status = statusFor(plan);

    var run = function (currentCart, pass) {
      var currentPlan = buildPlan(currentCart);
      if (!currentPlan) return Promise.resolve(currentCart);
      if (pass >= MAX_SYNC_PASSES) {
        console.warn(
          "[FreeGift] Gave up converging the cart after " +
            MAX_SYNC_PASSES +
            " passes.",
        );
        return Promise.resolve(currentCart);
      }
      return executePlan(currentPlan).then(function (nextCart) {
        return run(nextCart, pass + 1);
      });
    };

    state.inFlight = run(cart, 0)
      .then(function (finalCart) {
        state.inFlight = null;
        state.status = null;
        notify(finalCart);
        return finalCart;
      })
      .catch(function (error) {
        state.inFlight = null;
        state.status = null;
        console.error("[FreeGift] Sync failed.", error);
        return fetchCart()
          .then(function (finalCart) {
            notify(finalCart);
            return finalCart;
          })
          .catch(function () {
            return null;
          });
      });

    return state.inFlight;
  }

  function onChange(callback) {
    if (typeof callback === "function") state.listeners.push(callback);
  }

  function notify(cart) {
    state.listeners.forEach(function (callback) {
      try {
        callback(cart);
      } catch (e) {
        console.error("[FreeGift] onChange listener threw.", e);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  window.GoodrFreeGift = {
    config: config,
    isEnabled: function () {
      return !!config.enabled;
    },
    sync: sync,
    isGiftLine: isGiftLine,
    labelFor: labelFor,
    status: function () {
      return state.status;
    },
    registerRule: registerRule,
    onChange: onChange,
  };

  // Sync once on load so carts restored from a previous session (or changed
  // while the promotion was switched on/off) are corrected immediately.
  if (config.syncOnLoad) {
    var boot = function () {
      sync().then(function (cart) {
        // The static cart page is server rendered, so a change needs a reload
        // to be visible. The drawer re-renders through onChange instead.
        if (cart && config.isCartPage) window.location.reload();
      });
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", boot);
    } else {
      boot();
    }
  }
})(window, document);
